import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from '../../css/components/password-forgotten/passwordForgotten.css';
import { AppContext } from '../../Store';
import TextInput, { TEXT_INPUT_STYLES } from '../forms/TextInput';
import CTAButton from '../buttons/CTAButton';
import ErrorMessage, { ERROR_MESSAGE_STYLES } from '../forms/ErrorMessage';
import {
	resetPassword, isPasswordResetErrorResponse, isLongEnough, areEqual,
} from '../../helpers/password-forgotten/reset';
import { isEmpty, isTooShort } from '../../helpers/password-forgotten/common';
import { OneLPSsdkParamsContext } from '../../onelps-sdk-params-context';
import { DEFAULT_ERROR_CODE } from '../../helpers/server-messages';

export const STEP2_SERVER_ERROR_RELOAD_AFTER = 5000;
export const STEP2_SERVER_SUCCESS_RELOAD_AFTER = 5000;

function PasswordForgottenStepTwo ({ onSuccessCallback, showSuccessMessageInModal }) {
	const { t } = useTranslation('translation');
	const [state, dispatch] = useContext(AppContext);
	const onelpsParams = useContext(OneLPSsdkParamsContext);

	const [password1Value, setPassword1Value] = useState('');
	const [password2Value, setPassword2Value] = useState('');
	const [isErrorMessage1Shown, setIsErrorMessage1Shown] = useState(false);
	const [password1ErrorMessage, setPassword1ErrorMessage] = useState('');
	const [isErrorMessage2Shown, setIsErrorMessage2Shown] = useState(false);
	const [password2ErrorMessage, setPassword2ErrorMessage] = useState('');
	const [isSuccessMessageShownInModal, setIsSuccessMessageShownInModal] = useState(false);
	const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

	const showOnlyFirstError = () => {
		setIsErrorMessage1Shown(true);
		setIsErrorMessage2Shown(false);
	};

	const showOnlySecondError = () => {
		setIsErrorMessage1Shown(false);
		setIsErrorMessage2Shown(true);
	};

	const showBothErrors = () => {
		setIsErrorMessage1Shown(true);
		setIsErrorMessage2Shown(true);
	};

	const hideBothErrors = () => {
		setIsErrorMessage1Shown(false);
		setIsErrorMessage2Shown(false);
	};

	const pwMismatchError = t('passwordForgotten.changePassword.errorMessages.errorMessagePasswordsMismatch');
	const pwTooShortError = t('passwordForgotten.changePassword.errorMessages.errorMessageTooShort');
	const pw1EmptyError = t('passwordForgotten.changePassword.errorMessages.errorMessageInput1Empty');
	const pw2EmptyError = t('passwordForgotten.changePassword.errorMessages.errorMessageInput2Empty');

	function validateFrontend() {
		if (isLongEnough(password1Value) && isEmpty(password2Value)) {
			setPassword2ErrorMessage(pwMismatchError);
			showOnlySecondError();
			setShowLoadingIndicator(false);

			return false;
		}
		else if (isLongEnough(password1Value) && isLongEnough(password2Value) && !areEqual(password1Value, password2Value)) {
			setPassword2ErrorMessage(pwMismatchError);
			showOnlySecondError();
			setShowLoadingIndicator(false);

			return false;
		}
		else if (areEqual(password1Value, password2Value) && isTooShort(password1Value)) {
			setPassword1ErrorMessage(pwTooShortError);
			showOnlyFirstError();
			setShowLoadingIndicator(false);

			return false;
		}
		else if (isEmpty(password1Value) || isTooShort(password2Value)) {
			setPassword1ErrorMessage(pw1EmptyError);
			setPassword2ErrorMessage(pw2EmptyError);
			showBothErrors();
			setShowLoadingIndicator(false);

			return false;
		}

		return true;
	}

	async function validateBackend() {
		try {
			const result = await resetPassword(state.locale, onelpsParams.forgotPw.token, password1Value, dispatch);
			if (isPasswordResetErrorResponse(result)) {
				if (result.errors.length > 0) {
					let firstError = result.errors[0];
					const message = t(firstError.code) ?? firstError.message;

					setPassword1ErrorMessage(message);
					showOnlyFirstError();
				}
				setIsSuccessMessageShownInModal(false);
				return;
			}
			
			hideBothErrors();

			if (showSuccessMessageInModal) {
				setIsSuccessMessageShownInModal(true);
			} else {
				dispatch({ type: 'SET_SERVER_SUCCESS_MESSAGE', payload: t('passwordForgotten.changePassword.success') });
				dispatch({ type: 'SHOW_SERVER_SUCCESS' });
			}
			setShowLoadingIndicator(false);
			onSuccessCallback();

		} catch (error) {
			dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
			dispatch({ type: 'SHOW_SERVER_ERROR' });
			setIsSuccessMessageShownInModal(false);
		}
	}

	function validateInline(currentPassword2Value) {
		if (currentPassword2Value !== password1Value) {
			setPassword2ErrorMessage(pw2EmptyError);

			showOnlySecondError();
		} else {
			hideBothErrors();
		}
	}

	if (isSuccessMessageShownInModal) {
		return (
			<div
				className={styles.passwordForgottenWrapper}
				data-testid="successMessageStep2"
			>
				<p>{t('passwordForgotten.changePassword.success')}</p>
			</div>
		);
	}

	return (
		<form
			className={styles.passwordForgottenWrapper}
			onKeyDown={(e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					setShowLoadingIndicator(true);
					validateFrontend() && validateBackend();
				}
			}}
		>
			<p>{t('passwordForgotten.changePassword.main')}</p>
			<fieldset>
				<TextInput
					type="password"
					id="pop-up_pw-forgotten_new-password_input_password-1"
					labelText={t('passwordForgotten.changePassword.input1Label')}
					value={password1Value}
					onChangeCallback={(e) => setPassword1Value(e.target.value)}
					style={TEXT_INPUT_STYLES.PASSWORD_FORGOTTEN}
					disabled={showLoadingIndicator}
				/>
				{isErrorMessage1Shown &&
					<ErrorMessage
						text={password1ErrorMessage}
						style={ERROR_MESSAGE_STYLES.PASSWORD_FORGOTTEN}
					/>
				}
			</fieldset>
			<fieldset>
				<TextInput
					type="password"
					id="pop-up_pw-forgotten_new-password_input_password-2"
					labelText={t('passwordForgotten.changePassword.input2Label')}
					value={password2Value}
					onChangeCallback={(e) => {
						setPassword2Value(e.target.value);
						validateInline(e.target.value);
					}}
					style={TEXT_INPUT_STYLES.PASSWORD_FORGOTTEN}
					disabled={showLoadingIndicator}
				/>
				{isErrorMessage2Shown &&
					<ErrorMessage
						text={password2ErrorMessage}
						style={ERROR_MESSAGE_STYLES.PASSWORD_FORGOTTEN}
					/>
				}
			</fieldset>
			<CTAButton
				buttonLabel={t('passwordForgotten.changePassword.buttonLabel')}
				onButtonClick={() => {
					setShowLoadingIndicator(true);
					validateFrontend() && validateBackend();
				}}
				buttonId="pop-up_pw-forgotten_new-password_button_change-password"
				extraClassName={'passwordForgotten'}
				isDisabled={showLoadingIndicator}
			/>
		</form>
	);
}

PasswordForgottenStepTwo.propTypes = {
	onSuccessCallback: PropTypes.func.isRequired,
	showSuccessMessageInModal: PropTypes.bool.isRequired,
};

export default PasswordForgottenStepTwo;
